/* eslint-disable no-param-reassign */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { get, isEmpty, isEqual } from 'lodash';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import {
  DetailsList,
  DetailsRow,
  Dialog,
  DialogFooter,
  DialogType,
  MessageBar,
  Panel,
  PanelType,
  PrimaryButton,
  SelectionMode,
  DefaultButton,
  MessageBarType,
} from 'office-ui-fabric-react';
import { useBoolean } from '@uifabric/react-hooks';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleHeader from '../../components/SubModuleHeader';
import useAPI from '../../hooks/useAPI';
import { fetchContract, reorderAdvanceGroups } from '../../actions/contracts';
import Main from '../../components/Main';
import PageTitle from '../../components/PageTitle';
import styles from './AddAdvanceGroupsToContract.styles';
import AdvanceGroupForm from '../../components/AdvanceGroupForm';
import TextButton from '../../components/TextButton';
import SavingIndicator from '../../components/SavingIndicator';
import PauseTransactionsDialog from '../../components/PauseTransactionsDialog';
import AdvanceGroupCard from '../../components/AdvanceGroupCard';
import SectionHeader from '../../components/SectionHeader';
import Module from '../../components/Module';

const reOrderDialogContentProps = {
  type: DialogType.normal,
  title: 'Pausing Contract',
  subText: 'This action will pause the contract. Are you sure you would like to continue?',
};

const AddAdvanceGroupsToContract = () => {
  const history = useHistory();
  const formRef = useRef();
  const { contractAddress } = useParams();
  const { isLoading, error } = useAPI(() => fetchContract(contractAddress, { useCache: false }));
  const [isSaving, setisSaving] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formMode, setFormMode] = useState(AdvanceGroupForm.MODE.CREATE);
  const [advanceGroupToEdit, setAdvanceGroupToEdit] = useState({});
  const [isAdvanceGroupFormDialogVisible, setIsAdvanceGroupFormDialogVisible] = useState(false);
  const [isReorderDialogVisible, setIsReorderDialogVisible] = useState(false);
  const [items, setItems] = useState([]);

  const contractFetchErrorDetail = useMemo(() => {
    if (!error) {
      return null;
    }
    let errorTitle = 'Whoops!';
    let message = 'Something went wrong. Please refresh the page to try again.';
    if (error.status === 404) {
      message = 'This contract does not exist. Please try a different link.';
      errorTitle = 'Not Found';
    }
    return {
      title: errorTitle,
      message,
    };
  }, [error]);

  const [formPanelVisible, { setTrue: showFormPanel, setFalse: hideFormPanel }] = useBoolean(false);

  const onFormPanelDismissed = () => {
    hideFormPanel();
  };

  const contract = useSelector(state => get(state, `entities.contracts.${contractAddress}`, null));

  useEffect(() => {
    setItems(contract?.advanceGroups || []);
  }, [contract]);

  const handleContinue = () => {
    history.push(`/contracts/${contractAddress}`);
  };
  const handleCancel = () => history.push(`/contracts/${contractAddress}`);

  const reorderAdvanceGroupsApi = useAPI(
    requestBody => reorderAdvanceGroups(contractAddress, requestBody),
    [contract],
    {
      fetchOnMount: false,
      autoFetch: false,
      onSuccessCallback() {
        history.push(`/contracts/${contract.address}/`);
      },
    },
  );

  const submitToApi = async newAdvanceGroupsOrdered => {
    await reorderAdvanceGroupsApi.fetchApi({
      advanceGroups: newAdvanceGroupsOrdered?.map(ag => ag._id),
    });
  };

  const handleSubmitReOrder = () => {
    if (contract.approvalStatus && contract.approvalStatus.toLowerCase() === 'approved') {
      setIsReorderDialogVisible(true);
    } else {
      submitToApi(items).catch();
    }
  };

  const handleFailure = () => {
    setisSaving(false);
  };

  const handleFormSubmission = async () => {
    await formRef.current.submitForm();
    if (formRef.current && !isEmpty(formRef.current.errors)) {
      setisSaving(false);
      setIsAdvanceGroupFormDialogVisible(false);
    }
  };

  const handleSuccess = async () => {
    setisSaving(false);
    window.location.reload();
  };

  const handleSubmitFromDialog = async () => {
    setisSaving(true);
    formRef.current.setFieldValue('submitButtonType', 'Save');
    await handleFormSubmission();
  };

  const handleSaveAdvanceGroup = async () => {
    // show pause contract dialog only if associated contract is approved
    if (formRef.current.values?.contract?.approvalStatus?.toLowerCase() === 'approved') {
      setIsAdvanceGroupFormDialogVisible(true);
    } else {
      await handleSubmitFromDialog();
    }
  };

  const columns = [{ key: 'name', name: 'advanceGroupName', minWidth: 100 }];
  const isSubmitting = false;
  const onNewAdvanceGroupClick = () => {
    setFormTitle('Add Advance Group');
    setFormMode(AdvanceGroupForm.MODE.CREATE);
    showFormPanel();
  };

  const onEditAdvanceGroupClick = advanceGroup => {
    setFormTitle('Add Advance Group');
    setFormMode(AdvanceGroupForm.MODE.EDIT);
    setAdvanceGroupToEdit(advanceGroup);
    showFormPanel();
  };

  const swap = (firstIndex, secondIndex) => {
    const newItems = [...items];
    const tmp = newItems[firstIndex];
    newItems[firstIndex] = newItems[secondIndex];
    newItems[secondIndex] = tmp;
    setItems(newItems);
  };

  const onRenderItemColumn = (item, index) => {
    return (
      <div className={styles.advanceGroupCardModule}>
        <ModuleContent className={styles.cardContentWrapper}>
          <AdvanceGroupCard
            className={styles.advanceGroupCardModule}
            advanceGroup={item}
            index={index}
            openEditPanel={onEditAdvanceGroupClick}
            buttonType={AdvanceGroupCard.BUTTON.EDITINPANEL}
            enableUpArrow={!isSubmitting && index > 0}
            enableDownArrow={!isSubmitting && index < contract?.advanceGroups?.length - 1}
            upArrowClicked={() => swap(index, index - 1)}
            downArrowClicked={() => swap(index, index + 1)}
            showOrderingArrows
          />
        </ModuleContent>
      </div>
    );
  };

  let draggedItem;
  // eslint-disable-next-line no-unused-vars
  let draggedIndex = -1;

  const insertBeforeItem = item => {
    const insertIndex = items.indexOf(item);
    const newItems = items.filter(itm => draggedItem !== itm);
    newItems.splice(insertIndex, 0, draggedItem);
    setItems(newItems);
  };

  const getDragDropEvents = () => {
    return {
      canDrop: () => {
        return true;
      },
      canDrag: () => {
        return true;
      },
      onDrop: item => {
        if (draggedItem) {
          insertBeforeItem(item);
        }
      },
      onDragStart: (item, itemIndex) => {
        if (typeof item.startIndex === 'undefined') {
          // skip group item
          draggedItem = item;
          draggedIndex = itemIndex;
        }
      },
      onDragEnd: () => {
        draggedItem = undefined;
        draggedIndex = -1;
      },
    };
  };

  const dragDropEvents = getDragDropEvents();

  const showReorderButton = () => {
    if (items.length <= 1) {
      return false;
    }
    return !isEqual(contract.advanceGroups, items);
  };

  const renderAdvanceGroupList = () => {
    if (isLoading || !contract) {
      return (
        <Spinner
          size={SpinnerSize.medium}
          className={styles.loadingSpinner}
          data-testid="advanceGroupSpinner"
        />
      );
    }

    return (
      <div>
        <DetailsList
          items={items}
          columns={columns}
          selectionMode={SelectionMode.none}
          onRenderItemColumn={onRenderItemColumn}
          dragDropEvents={dragDropEvents}
          isHeaderVisible={false}
          onRenderRow={props => <DetailsRow {...props} className={styles.detailsRowStyle} />}
        />

        {/* BUTTONS */}
        <div className={styles.buttonContainer}>
          <TextButton
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </TextButton>
          {showReorderButton() ? (
            <PrimaryButton
              onClick={handleSubmitReOrder}
              disabled={isSubmitting}
              data-testid="applyReorderButton"
            >
              Apply Re-order
            </PrimaryButton>
          ) : (
            <PrimaryButton onClick={handleContinue} disabled={isSubmitting}>
              Continue
            </PrimaryButton>
          )}
        </div>
      </div>
    );
  };

  if (contractFetchErrorDetail) {
    return (
      <>
        <PageTitle>{contractFetchErrorDetail.title}</PageTitle>
        <Main>
          <Text>{contractFetchErrorDetail.message}</Text>
        </Main>
      </>
    );
  }

  const createOrAddErrorMessage = err => {
    return err.message
      ? err.message
      : 'Something went wrong when saving new order. Please try refreshing the page.';
  };

  return (
    <>
      <PageTitle>Add Contract</PageTitle>
      <Main>
        {reorderAdvanceGroupsApi.error && (
          <MessageBar
            isMultiline={false}
            messageBarType={MessageBarType.error}
            messageBarIconProps={{ iconName: 'Info' }}
          >
            {createOrAddErrorMessage(reorderAdvanceGroupsApi.error)}
          </MessageBar>
        )}
        <MessageBar>
          <div>
            <Text as="p" className={styles.messageBarTitle}>
              Advance Groups are optional
            </Text>
          </div>
          <Text as="p" block className={styles.messageBarBody}>
            Note: Not all contracts require Advance Groups. If your contract does not require
            Advance Groups, then please skip this step. If you are unsure, you can skip this step
            for now.
          </Text>
        </MessageBar>
        <br />
        <SectionHeader noBorder as="h2">
          Advance Groups
        </SectionHeader>

        <div>
          <Module className={styles.newAdvanceGroupModule}>
            <ModuleContent>
              <SubModule
                disableAccordian
                closed
                disabled={isSubmitting}
                className={clsx(
                  styles.addFeatureButtonSectionHeader,
                  isSubmitting && styles.addFeatureButtonSectionHeaderDisabled,
                )}
                onClick={onNewAdvanceGroupClick}
                data-testid="addAdvanceGroupTestId"
              >
                <SubModuleHeader
                  variant="large"
                  extra={
                    <IconButton
                      iconProps={{ iconName: 'Add' }}
                      title="Add an advance group"
                      ariaLabel="Add an advance group"
                    />
                  }
                >
                  Create a new Advance Group
                </SubModuleHeader>
              </SubModule>
            </ModuleContent>
          </Module>
        </div>
        {renderAdvanceGroupList()}
      </Main>
      <Panel
        isOpen={formPanelVisible}
        onDismiss={onFormPanelDismissed}
        closeButtonAriaLabel="Close"
        headerText={formTitle}
        className={styles.panelHeading}
        type={PanelType.smallFluid}
        onOuterClick={() => {}}
        focusTrapZoneProps={{ disableFirstFocus: true }}
        data-testid="advanceGroupFormPanelTestId"
      >
        <AdvanceGroupForm
          ref={formRef}
          onSuccess={handleSuccess}
          onFailure={handleFailure}
          initialContract={contract}
          mode={formMode}
          advanceGroupToEdit={advanceGroupToEdit}
        />

        <div className={styles.buttonContainer}>
          <TextButton className={styles.cancelButton} onClick={handleCancel}>
            Cancel
          </TextButton>
          {!isSaving ? (
            <PrimaryButton
              className={styles.saveButton}
              onClick={handleSaveAdvanceGroup}
              disabled={isSaving}
              data-testid="AdvanceGroupFormSaveDraft"
            >
              Save
            </PrimaryButton>
          ) : (
            <SavingIndicator data-testid="saveLoader" className={styles.saveButton} />
          )}
        </div>

        <PauseTransactionsDialog
          onDismiss={() => setIsAdvanceGroupFormDialogVisible(false)}
          onSubmit={handleSubmitFromDialog}
          hidden={!isAdvanceGroupFormDialogVisible}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Edits to group assignment may cause reprocessing',
            closeButtonAriaLabel: 'Close',
          }}
          isAdvanceGroup
        />
      </Panel>

      <Dialog hidden={!isReorderDialogVisible} dialogContentProps={reOrderDialogContentProps}>
        <DialogFooter>
          <PrimaryButton
            text="Continue"
            data-testid="reOrderAdvanceGroupConfirmation"
            onClick={() => {
              setIsReorderDialogVisible(false);
              submitToApi(items).catch();
            }}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => {
              setIsReorderDialogVisible(false);
            }}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default AddAdvanceGroupsToContract;
